import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'

import {
  AcknowledgeModal,
  FormField,
  Select,
  Spacer,
  TextInput,
  Modal,
  ActionModal,
  Typography
} from '@percent/lemonade'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useMutation } from '@percent/utility'
import { WebsiteInvalidReason, websiteInvalidReasons } from '@percent/admin-dashboard/api/types'
import { ReviewWebsiteModalProps } from './ReviewWebsiteModal.types'

type FormValues = {
  isValid: 'valid' | 'invalid'
  invalidReason: WebsiteInvalidReason | ''
  newWebsite: string
}

export function ReviewWebsiteModal({ validation, refresh, isModalOpened, setModalOpened }: ReviewWebsiteModalProps) {
  const { t } = useTranslation()
  const { adminService } = useServices()
  const [errorDialogState, setErrorDialogState] = useState(false)

  const invalidReasonOptions = websiteInvalidReasons.map(reason => ({
    value: reason,
    label: t(`typography.websiteStatus.invalid.${reason}`)
  }))

  const validityOptions = [
    { value: 'valid', label: t('typography.websiteStatus.select.valid') },
    { value: 'invalid', label: t('typography.websiteStatus.select.invalid') }
  ]

  const [{ isPending, success }, { apiFunc }] = useMutation(
    adminService.reviewValidationRequestWebsite,
    () => {},
    () => {
      setErrorDialogState(true)
    }
  )

  const schema = yup.object().shape({
    isValid: yup.string().oneOf(['valid', 'invalid']).required(),
    invalidReason: yup.string().when('isValid', {
      is: 'invalid',
      then: s => s.oneOf(websiteInvalidReasons as unknown as string[]).required(),
      otherwise: s => s.optional()
    }),
    newWebsite: yup.string().url(t('errorMessage.validWebsite')).optional()
  })

  const {
    setValue,
    watch,
    reset,
    handleSubmit,
    formState: { isValid, errors }
  } = useForm<FormValues>({
    defaultValues: { isValid: '' as unknown as FormValues['isValid'], invalidReason: '', newWebsite: '' },
    mode: 'onChange',
    resolver: yupResolver(schema) as never
  })

  const isValidValue = watch('isValid')
  const invalidReasonValue = watch('invalidReason')

  const handleClose = () => {
    setModalOpened(false)
    setErrorDialogState(false)
    reset()
  }

  const handleCloseAfterSuccess = () => {
    handleClose()
    refresh()
  }

  const handleErrorClose = () => {
    setErrorDialogState(false)
  }

  const onSubmit = ({ isValid: validity, invalidReason, newWebsite }: FormValues) => {
    if (!validation) return

    const payload =
      validity === 'valid'
        ? { verifiedWebsite: validation.website, invalidReason: null }
        : { verifiedWebsite: newWebsite || null, invalidReason: invalidReason as WebsiteInvalidReason }

    apiFunc({ id: validation.id, payload })
  }

  const successOrErrorModal =
    success && !errorDialogState ? (
      <AcknowledgeModal
        result="positive"
        title={t('dialog.reviewWebsite.success.title')}
        description={t('dialog.reviewWebsite.success.description')}
        buttonText={t('button.close')}
        handleClose={handleCloseAfterSuccess}
        viewTestId="review-website-success-modal"
        buttonTestId="review-website-success-button"
      />
    ) : (
      <AcknowledgeModal
        result="negative"
        title={t('dialog.reviewWebsite.error.title')}
        description={t('dialog.reviewWebsite.error.description')}
        buttonText={t('button.close')}
        handleClose={handleErrorClose}
        viewTestId="review-website-error-modal"
        buttonTestId="review-website-error-button"
      />
    )

  return (
    <Modal open={isModalOpened} onClose={handleClose} aria-labelledby="review-website-modal">
      {!success && !errorDialogState ? (
        <ActionModal
          title={t('dialog.reviewWebsite.title')}
          primaryButtonText={t('dialog.reviewWebsite.submitReview')}
          secondaryButtonText={t('button.cancel')}
          type="submit"
          variant={isValid ? 'primary' : 'secondary'}
          disabled={!isValid || isPending}
          loading={isPending}
          handleClose={handleClose}
          handleSubmit={handleSubmit(onSubmit)}
          primaryBtnTestId="review-website-submit-button"
          secondaryBtnTestId="review-website-cancel-button"
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <Typography>{t('dialog.reviewWebsite.description')}</Typography>
            <Spacer size={4} axis="vertical" />
            <FormField label={t('dialog.reviewWebsite.submittedWebsite')} data-testid="submitted-website">
              <TextInput
                name="website"
                value={validation?.website ?? ''}
                disabled
                style={{ pointerEvents: 'none', backgroundColor: '#F1F0F2' }}
              />
            </FormField>
            <Spacer size={4} axis="vertical" />
            <FormField label={t('dialog.reviewWebsite.isValid')} necessity="required" data-testid="is-valid-select">
              <Select
                placeholder={t('dialog.reviewWebsite.selectValidity')}
                options={validityOptions}
                defaultValue={validityOptions.find(o => o.value === isValidValue)}
                onChange={option => {
                  setValue('isValid', option.value as FormValues['isValid'], { shouldValidate: true })

                  if (option.value === 'valid') {
                    setValue('invalidReason', '', { shouldValidate: true })
                    setValue('newWebsite', '', { shouldValidate: true })
                  }
                }}
              />
            </FormField>
            {isValidValue === 'invalid' && (
              <>
                <Spacer size={4} axis="vertical" />
                <FormField
                  label={t('dialog.reviewWebsite.invalidReason')}
                  necessity="required"
                  data-testid="invalid-reason-select"
                >
                  <Select
                    placeholder={t('dialog.reviewWebsite.selectReason')}
                    options={invalidReasonOptions}
                    defaultValue={invalidReasonOptions.find(o => o.value === invalidReasonValue)}
                    onChange={option => {
                      setValue('invalidReason', option.value as WebsiteInvalidReason, { shouldValidate: true })
                    }}
                  />
                </FormField>
                <Spacer size={4} axis="vertical" />
                <FormField
                  label={t('dialog.reviewWebsite.newWebsite')}
                  status={errors.newWebsite ? 'danger' : 'default'}
                  statusMessage={errors.newWebsite?.message}
                  data-testid="new-website"
                >
                  <TextInput
                    name="newWebsite"
                    placeholder={t('dialog.reviewWebsite.newWebsitePlaceholder')}
                    value={watch('newWebsite')}
                    onChange={e => setValue('newWebsite', e.target.value, { shouldValidate: true })}
                  />
                </FormField>
                <Spacer size={2} axis="vertical" />
                <Typography>{t('dialog.reviewWebsite.newWebsiteDescription')}</Typography>
              </>
            )}
          </form>
        </ActionModal>
      ) : (
        successOrErrorModal
      )}
    </Modal>
  )
}
